import { BybTab, BybTabBar } from '@Components/common/byb-ds';
import BybTextFieldAlt from '@Components/game/common/input/text-field/byb-text-field-alt';
import { usePermissions } from '@Context/common/permissions/permissions';
import useI18n from '@i18n';
import { Box, Divider, Stack, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import '@pixi/events';
import { KitRecord } from '@Shared/schemas/avatars/avatarSchemas';
import { MuiColorInput, MuiColorInputProps } from 'mui-color-input';
import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import KitPixyComponent from './kit-pixy-component';

const MuiColorInputStyled = styled((props: MuiColorInputProps) => (
  <MuiColorInput
    {...props}
    PopoverProps={{
      anchorOrigin: {
        vertical: "center",
        horizontal: "right",
      },
    }}
  />
))(
  ({ theme }) => `
  width: 50px;
  height: 50px;
  background-color: #C2C2C2;
  border-radius: 25px;
  
  & .MuiColorInput-Button {
    width: 50px;
    height: 50px;
    border-radius: 25px;
    padding: 0px;
    corner-shape: inherit;
  }
    
  & .MuiInputBase-root {
    padding: 0px;
    height: 50px;
    width: 50px;
    outline: 2px;
    outline-color: ${theme.palette.dsSurface.v400};
    outline-style: solid;
    border-radius: 25px;
  }
`
);

const KitSelector = ({
  gender,
  kit,
  onChange,
  title,
  width,
}: {
  gender: string;
  kit: KitRecord;
  onChange?: (kit: KitRecord) => void;
  title?: string;
  width?: number;
}) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const [kitName, setKitName] = useState<string>(kit.name);
  const [playerPrimary, setPlayerPrimary] = useState<string>(kit.primary);
  const [playerSecondary, setPlayerSecondary] = useState<string>(kit.secondary);
  const [playerTertiary, setPlayerTertiary] = useState<string>(kit.tertiary);
  const [goalkeeperPrimary, setGoalkeeperPrimary] = useState<string>(kit.goalkeeper_primary);
  const [goalkeeperSecondary, setGoalkeeperSecondary] = useState<string>(kit.goalkeeper_secondary);
  const [goalkeeperTertiary, setGoalkeeperTertiary] = useState<string>(kit.goalkeeper_tertiary);
  const [position, setPosition] = useState<number>(0);
  const [isPlayer, setIsPlayer] = useState<boolean>(true);
  const [currentKit, setCurrentKit] = useState<KitRecord>(kit);
  const [debouncedKit, setDebouncedKit] = useState<KitRecord>(kit);
  const height = (5 / 3) * (width || 300);
  const permissions = usePermissions?.();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const fontSize = isSmDown ? 'medium' : 'large';

  const isDisabled = !permissions?.canEdit;

  // Whenever any colour changes, update the current kit
  useEffect(() => {
    const newKit = {
      ...kit,
      name: kitName,
      primary: playerPrimary,
      secondary: playerSecondary,
      tertiary: playerTertiary,
      goalkeeper_primary: goalkeeperPrimary,
      goalkeeper_secondary: goalkeeperSecondary,
      goalkeeper_tertiary: goalkeeperTertiary,
    };

    setCurrentKit(newKit);
  }, [
    kitName,
    playerPrimary,
    playerSecondary,
    playerTertiary,
    goalkeeperPrimary,
    goalkeeperSecondary,
    goalkeeperTertiary,
  ]);

  useEffect(() => {
    setKitName(kit.name);
    setPlayerPrimary(kit.primary);
    setPlayerSecondary(kit.secondary);
    setPlayerTertiary(kit.tertiary);
    setGoalkeeperPrimary(kit.goalkeeper_primary);
    setGoalkeeperSecondary(kit.goalkeeper_secondary);
    setGoalkeeperTertiary(kit.goalkeeper_tertiary);
  }, [kit]);

  // Colour changes cause many state changes, so debounce the kit changes for propogation
  const [, cancel] = useDebounce(
    () => {
      setDebouncedKit(currentKit);
    },
    200,
    [currentKit]
  );

  // Whenever the debounced kit changes, propagate to the parent
  useEffect(() => {
    if (onChange) onChange(debouncedKit);
  }, [debouncedKit]);

  // Whenever the position changes, update the player state
  useEffect(() => {
    setIsPlayer(position === 0);
  }, [position]);

  return (
    <Box sx={{ borderRadius: theme.spacing(2) }}>
      <Stack spacing={theme.spacing(3)}>
        {title && (
          <Typography variant="text1xl600" color={theme.palette.dsBase.v200} sx={{ paddingLeft: theme.spacing(1) }}>
            {title}
          </Typography>
        )}
        <BybTextFieldAlt
          value={kitName}
          sx={{ margin: '0 20px!important' }}
          onChange={(e) => {
            setKitName(e.target.value);
          }}
        />
        <Stack justifyContent={'center'} alignItems={'center'} spacing={theme.spacing(5.5)}>
          <Stack spacing={theme.spacing(1)} sx={{ width: '100%' }}>
            <BybTabBar
              value={position}
              onChange={(e, v) => {
                setPosition(v);
              }}
            >
              <BybTab value={0} label={i18n('position.players')} index={0} size={fontSize} />
              <BybTab value={1} label={i18n('position.goalkeeper')} index={1} size={fontSize} />
            </BybTabBar>
            <Box sx={{ width: '100%' }}>
              <Divider variant="middle" sx={{ borderBottomWidth: '2px' }} />
            </Box>
          </Stack>
          <Stack direction="row" spacing={3}>
            <KitPixyComponent
              width={width || 300}
              height={height}
              gender={gender}
              isPlayer={isPlayer}
              playerPrimary={playerPrimary}
              playerSecondary={playerSecondary}
              playerTertiary={playerTertiary}
              goalkeeperPrimary={goalkeeperPrimary}
              goalkeeperSecondary={goalkeeperSecondary}
              goalkeeperTertiary={goalkeeperTertiary}
            />

            <Stack
              spacing={theme.spacing(4)}
              alignItems={'center'}
              alignContent={'center'}
              justifyContent={'start'}
              sx={{ pt: 3 }}
            >
              <MuiColorInputStyled
                disabled={isDisabled}
                isAlphaHidden
                format="hex"
                value={isPlayer ? playerPrimary : goalkeeperPrimary}
                onChange={isPlayer ? setPlayerPrimary : setGoalkeeperPrimary}
              />
              <MuiColorInputStyled
                disabled={isDisabled}
                isAlphaHidden
                format="hex"
                value={isPlayer ? playerSecondary : goalkeeperSecondary}
                onChange={isPlayer ? setPlayerSecondary : setGoalkeeperSecondary}
                sx={{ width: '52px', backgroundColor: '#C2C2C2' }}
              />
              <MuiColorInputStyled
                disabled={isDisabled}
                isAlphaHidden
                format="hex"
                value={isPlayer ? playerTertiary : goalkeeperTertiary}
                onChange={isPlayer ? setPlayerTertiary : setGoalkeeperTertiary}
                sx={{ width: '52px', backgroundColor: '#C2C2C2' }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default KitSelector;
